import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Topbar from "@/components/layout/Topbar";
import { ShieldCheck, ShieldAlert, Loader2, Copy, KeyRound, Smartphone, Monitor, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatDateTime } from "@/lib/format";

/**
 * Two-factor authentication settings page.
 * Mandatory for super admins (cannot disable); optional for hotel_admin,
 * property_manager and other roles. Reception / housekeeping cannot see this page.
 */
export default function SecuritySettings() {
    const { user } = useAuth();
    const [status, setStatus] = useState(null);
    const [step, setStep] = useState("idle"); // idle | setup | confirm | done
    const [setup, setSetup] = useState(null);
    const [code, setCode] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [recoveryCodes, setRecoveryCodes] = useState(null);
    const [devices, setDevices] = useState([]);

    const load = () => api.get("/auth/2fa/status").then(({ data }) => setStatus(data));
    const loadDevices = () => api.get("/auth/2fa/trusted-devices").then(({ data }) => setDevices(data));
    useEffect(() => { load(); loadDevices(); }, []);

    const beginSetup = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/auth/2fa/setup");
            setSetup(data);
            setStep("setup");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const confirmEnable = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/auth/2fa/enable", { code, password });
            setRecoveryCodes(data.recovery_codes);
            setStep("done");
            setCode(""); setPassword("");
            toast.success("Two-factor authentication enabled");
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invalid code"); }
        finally { setBusy(false); }
    };

    const disable = async () => {
        const c = window.prompt("Enter your current 2FA code (or a recovery code) to disable:");
        if (!c) return;
        const p = window.prompt("Confirm with your password:");
        if (!p) return;
        setBusy(true);
        try {
            await api.post("/auth/2fa/disable", { code: c.trim(), password: p });
            toast.success("Two-factor authentication disabled");
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const revokeDevice = async (id) => {
        if (!window.confirm("Revoke this trusted device? It will need to complete 2FA again next time.")) return;
        try {
            await api.delete(`/auth/2fa/trusted-devices/${id}`);
            toast.success("Device revoked");
            await loadDevices();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const revokeAllDevices = async () => {
        if (!window.confirm("Revoke ALL trusted devices? You'll be asked for a 2FA code on your next sign-in everywhere.")) return;
        try {
            await api.post("/auth/2fa/trusted-devices/revoke-all");
            toast.success("All trusted devices revoked");
            await loadDevices();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    if (!status) return <div className="p-8 text-slate-500">Loading…</div>;

    return (
        <>
            <Topbar title="Security" subtitle="Protect your account with two-factor authentication" />
            <div className="mx-auto max-w-3xl flex-1 overflow-auto p-6">
                <div className="akm-card p-6">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                            <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${status.enabled ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                                {status.enabled ? <ShieldCheck size={22} /> : <ShieldAlert size={22} />}
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h3 className="text-lg font-semibold text-foreground">Two-factor authentication</h3>
                                    {status.mandatory && (
                                        <span className="rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#F26322]">Required</span>
                                    )}
                                </div>
                                <p className="mt-1 text-[13px] text-muted-foreground">
                                    {status.enabled
                                        ? "Your account is protected by an authenticator app."
                                        : status.mandatory
                                            ? "As a Super Admin, you must enable 2FA on your account."
                                            : "Add an extra layer of security using an authenticator app (Google Authenticator, 1Password, Authy)."}
                                </p>
                            </div>
                        </div>
                        {!status.enabled ? (
                            <button data-testid="2fa-enable-btn" onClick={beginSetup} disabled={busy} className="akm-btn-primary h-9 disabled:opacity-50">
                                {busy ? <Loader2 size={14} className="animate-spin" /> : "Enable"}
                            </button>
                        ) : !status.mandatory ? (
                            <button data-testid="2fa-disable-btn" onClick={disable} disabled={busy} className="akm-btn-ghost h-9 text-red-600 hover:bg-red-50 disabled:opacity-50">
                                Disable
                            </button>
                        ) : null}                    </div>

                    {step === "setup" && setup && (
                        <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5">
                            <div className="flex flex-col items-center gap-4 md:flex-row md:items-start">
                                <div className="shrink-0 rounded-xl bg-white p-3 shadow-sm">
                                    <img src={setup.qr_png_base64} alt="2FA QR" className="h-44 w-44" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="flex items-center gap-2 text-[14px] font-semibold"><Smartphone size={14} /> Scan with your authenticator app</h4>
                                    <p className="mt-2 text-[13px] text-muted-foreground">
                                        Open Google Authenticator (or similar), tap “+” and scan this QR code.
                                    </p>
                                    <details className="mt-3 text-[12px] text-muted-foreground">
                                        <summary className="cursor-pointer">Or enter this key manually</summary>
                                        <div className="mt-2 flex items-center gap-2">
                                            <code className="flex-1 rounded bg-white px-2 py-1 font-mono text-[12px] text-slate-800">{setup.secret}</code>
                                            <button onClick={() => { navigator.clipboard.writeText(setup.secret); toast.success("Copied"); }} className="akm-btn-ghost h-7 px-2"><Copy size={11} /></button>
                                        </div>
                                    </details>
                                    <form onSubmit={confirmEnable} className="mt-4 space-y-2.5">
                                        <input
                                            data-testid="2fa-setup-code"
                                            value={code}
                                            onChange={(e) => setCode(e.target.value)}
                                            placeholder="6-digit code from app"
                                            required
                                            inputMode="numeric"
                                            maxLength={6}
                                            className="akm-input h-10 w-full text-center font-mono text-[16px] tracking-widest"
                                        />
                                        <input
                                            data-testid="2fa-setup-password"
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Your account password"
                                            required
                                            className="akm-input h-10 w-full"
                                        />
                                        <button data-testid="2fa-confirm" type="submit" disabled={busy} className="akm-btn-primary h-10 w-full disabled:opacity-50">
                                            {busy ? <Loader2 size={14} className="animate-spin" /> : "Confirm & Enable"}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )}

                    {step === "done" && recoveryCodes && (
                        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-5">
                            <div className="flex items-center gap-2 text-amber-900">
                                <KeyRound size={16} /> <strong>Save your recovery codes</strong>
                            </div>
                            <p className="mt-1 text-[13px] text-amber-800">
                                Each code can be used once if you lose access to your authenticator. Store them in a password manager.
                            </p>
                            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
                                {recoveryCodes.map((c) => (
                                    <code key={c} className="rounded bg-white px-2 py-1.5 text-center font-mono text-[13px] text-slate-800">{c}</code>
                                ))}
                            </div>
                            <div className="mt-3 flex justify-end">
                                <button onClick={() => { navigator.clipboard.writeText(recoveryCodes.join("\n")); toast.success("All codes copied"); }} className="akm-btn-ghost h-8 text-[12px]">
                                    <Copy size={11} /> Copy all
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Trusted devices */}
                <div className="akm-card mt-6 p-6" data-testid="trusted-devices-card">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <h3 className="text-lg font-semibold text-foreground">Trusted devices</h3>
                            <p className="mt-1 text-[13px] text-muted-foreground">
                                Devices you&apos;ve chosen to trust skip the 2FA code for 30 days. Revoke any you don&apos;t recognize.
                            </p>
                        </div>
                        {devices.some((d) => d.is_active) && (
                            <button
                                onClick={revokeAllDevices}
                                data-testid="revoke-all-devices"
                                className="akm-btn-ghost h-9 text-red-600 hover:bg-red-50"
                            >
                                Revoke all
                            </button>
                        )}
                    </div>
                    <div className="mt-4">
                        {devices.length === 0 ? (
                            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 py-8 text-center text-[13px] text-muted-foreground">
                                No trusted devices yet. Check &quot;Remember this device&quot; the next time you sign in with 2FA.
                            </div>
                        ) : (
                            <ul className="divide-y divide-slate-100">
                                {devices.map((d) => (
                                    <li key={d.id} className="flex items-center gap-3 py-3" data-testid={`device-${d.id}`}>
                                        <div className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${
                                            d.is_active ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"
                                        }`}>
                                            <Monitor size={16} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <div className="text-[13.5px] font-semibold text-slate-900">{d.device_name}</div>
                                                {d.is_current && (
                                                    <span className="rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#F26322]">
                                                        This device
                                                    </span>
                                                )}
                                                {!d.is_active && (
                                                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                                        Revoked
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-0.5 truncate text-[11.5px] text-slate-500">
                                                {d.ip || "—"} · added {formatDateTime(d.created_at)} · expires {formatDate(d.expires_at)}
                                            </div>
                                        </div>
                                        {d.is_active && (
                                            <button
                                                onClick={() => revokeDevice(d.id)}
                                                data-testid={`revoke-device-${d.id}`}
                                                className="rounded-lg p-2 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                                                title="Revoke"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
